/**
 * GuestBanner - Slim banner shown on guest-accessible screens.
 * Encourages sign-up with a clear CTA. Dismissible per session.
 *
 * Tracking: fires GUEST_SIGNUP_PROMPT_SHOWN once per mount when visible to a guest, so the
 * "prompt shown" funnel counts banner impressions (not just the CoinStore dialog). The visible
 * markup lives in an inner component so the impression effect's hooks stay unconditional.
 */

import { useEffect } from 'react';
import { View, Text, Pressable } from 'react-native';
import { X } from 'lucide-react-native';
import { useRouter } from 'expo-router';
import { useSupabaseAuth } from '../../contexts/SupabaseAuthContext';
import { useAnalytics } from '../../../analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../../analytics';
import { buildPublicSignInHref } from '../../../signIn/helpers/postAuthRedirect.helper';
import { guestBannerStyles } from './GuestBanner.styles';
import { GUEST_BANNER_ACTION, GUEST_BANNER_COPY, GUEST_BANNER_TEST_IDS } from './GuestBanner.constants';
import { useGuestBannerDismissStore } from './guestBannerDismissStore';
import { theme } from '../../../../theme';

export function GuestBanner() {
  const { isAnonymous } = useSupabaseAuth();
  const dismissed = useGuestBannerDismissStore((s) => s.dismissed);
  const dismiss = useGuestBannerDismissStore((s) => s.dismiss);

  if (!isAnonymous || dismissed) return null;

  return <GuestBannerVisible onDismiss={dismiss} />;
}

function GuestBannerVisible({ onDismiss }: { onDismiss: () => void }) {
  const router = useRouter();
  const { track } = useAnalytics();

  // Fire-once impression: runs on mount only, so re-renders never double-count.
  useEffect(() => {
    track(ANALYTICS_EVENTS.GUEST_SIGNUP_PROMPT_SHOWN, { action: GUEST_BANNER_ACTION });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignUp = () => {
    track(ANALYTICS_EVENTS.GUEST_SIGNUP_PROMPT_ACCEPTED, { action: GUEST_BANNER_ACTION });
    router.replace(buildPublicSignInHref({ signup: true }) as `/${string}`);
  };

  return (
    <View style={guestBannerStyles.container} testID={GUEST_BANNER_TEST_IDS.CONTAINER}>
      <Text style={guestBannerStyles.text}>
        {GUEST_BANNER_COPY}
      </Text>
      <Pressable
        testID={GUEST_BANNER_TEST_IDS.SIGN_UP}
        style={({ pressed }) => [guestBannerStyles.signUpButton, pressed && guestBannerStyles.signUpButtonPressed]}
        onPress={handleSignUp}
      >
        <Text style={guestBannerStyles.signUpButtonText}>Sign Up</Text>
      </Pressable>
      <Pressable
        testID={GUEST_BANNER_TEST_IDS.DISMISS}
        style={({ pressed }) => [guestBannerStyles.dismissButton, pressed && guestBannerStyles.dismissButtonPressed]}
        onPress={onDismiss}
      >
        <X size={16} color={theme.white} />
      </Pressable>
    </View>
  );
}
